// tslint:disable: max-line-length
import * as React from 'react'
import { Provider, useSelector } from 'react-redux'
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useParams,
} from 'react-router-dom'

import store from './store'
import Auth from './auth/Auth'
import Login from './auth/Login'
import Header from './common/header/Header'
import AccountsList from './accounts/AccountsList'
import AccountBranches from './accounts/accountBranches/AccountBranches'
import InternalUsers from './users/InternalUsers'
import InternalMessages from './messages/InternalMessages'
import Species from './species/Species'
import AccountUsers from './accounts/accountUsers/AccountUsers'
import AccountSettings from './accounts/accountSettings/AccountSettings'
import AccountContext from './accounts/AccountContext'
import Meta from './auth/Meta'
import ManagerUsers from './manager/users/Users'
import ManagerGroups from './manager/managerGroups'
import ManagerInventory from './manager/inventory/Inventory'
import ManagerInboundInventories from './manager/inboundInventories'
import ManagerInboundInventory from './manager/inboundInventory'
import ManagerOrders from './manager/orders/Orders'
import ManagerOrder from './manager/orders/Order/Order'
import ShoppingCart from './manager/orders/Order/ShoppingCart'
import { getShouldRenderTruckInventoryFeature } from './common/getShouldRenderTruckInventoryFeature'
import { getShouldRenderBinInventoryFeature } from './common/getShouldRenderBinInventoryFeature'
import Locations from './accounts/locations/Locations'
import LocationForm from './accounts/locations/LocationForm'
import PowerBiEmbed from './manager/powerbi'
import Docs from './docs/Docs'
import DesignCatalog from './designCatalog/DesignCatalog'
import { CycleCounts, CycleCountEdit } from './accounts/cycleCounts'
import LocationInventory from './accounts/locations/LocationInventory'
import Plan from './plan/Plan'
import PlanExportsEmbed from './foresight/PlanExportsEmbed'
import Order from './orders/Order'
import Orders from './orders/Orders'
import Invoices from './invoices/Invoices'
import { managerBranchSelector } from './manager/managerSelectors'
import { IUser } from './types/user'
import WebChat from './components/WebChat'

const ManagerRoutes: React.FC<{ user: IUser }> = ({ user }) => {
  const managerBranch = useSelector(managerBranchSelector)
  const shouldRenderTruckInventoryFeature =
    getShouldRenderTruckInventoryFeature(
      user?.account?.truckInventoryFeature,
      managerBranch?.truckInventoryFeature,
    )
  const shouldRenderBinInventoryFeature = getShouldRenderBinInventoryFeature(
    user?.account?.binInventoryFeature,
    managerBranch?.binInventoryFeature,
  )

  return (
    <Routes>
      <Route path="/users" element={<ManagerUsers />} />
      <Route path="/groups" element={<ManagerGroups />} />
      <Route
        path="/inbound_inventories/:inboundInventoryId"
        element={<ManagerInboundInventory />}
      />
      <Route
        path="/inbound_inventories"
        element={<ManagerInboundInventories />}
      />
      {(shouldRenderTruckInventoryFeature ||
        shouldRenderBinInventoryFeature) && (
        <>
          <Route path="/locations" element={<Locations />} />
          <Route path="/locations-add" element={<LocationForm />} />
          <Route
            path="/locations/:locationId"
            element={<LocationInventory />}
          />
          <Route
            path="/locations/:locationId/edit"
            element={<LocationForm />}
          />
          <Route path="/cycle_counts" element={<CycleCounts />} />
          <Route
            path="/cycle_counts/:cycleCountId"
            element={<CycleCountEdit />}
          />
        </>
      )}
      <Route path="/place_order" element={<ShoppingCart />} />
      <Route path="/inventory" element={<ManagerInventory />} />
      <Route path="/plan/:productId?" element={<Plan />} />
      <Route path="/plan-exports" element={<PlanExportsEmbed />} />
      <Route path="/orders/:orderId" element={<ManagerOrder />} />
      <Route path="/orders" element={<ManagerOrders />} />
      <Route path="/requests" element={<Navigate to="/orders" replace />} />
      <Route path="/powerbi" element={<PowerBiEmbed />} />
      <Route path="/docs" element={<Docs />} />
      <Route path="/design-catalog" element={<DesignCatalog />} />
      <Route
        path="/"
        element={
          <Navigate
            to={user.generalManager ? '/orders' : '/inventory'}
            replace
          />
        }
      />
    </Routes>
  )
}

// Provides account id to descendants from the URL, matching the legacy render-prop.
const ClientAccountLayout: React.FC = () => {
  const { accountId } = useParams()

  return (
    <AccountContext.Provider value={Number(accountId)}>
      <Outlet />
    </AccountContext.Provider>
  )
}

const BranchRootRedirect: React.FC = () => {
  const { accountId, branchId } = useParams()

  return (
    <Navigate to={`/clients/${accountId}/branches/${branchId}/plan`} replace />
  )
}

const BranchRequestsRedirect: React.FC = () => {
  const { accountId, branchId } = useParams()

  return (
    <Navigate
      to={`/clients/${accountId}/branches/${branchId}/orders`}
      replace
    />
  )
}

const InternalRoutes: React.FC<{ user: IUser }> = ({ user }) => (
  <Routes>
    <Route path="/clients" element={<ClientAccountLayout />}>
      <Route index element={<AccountsList />} />
      <Route path=":accountId" element={<AccountBranches />} />
      <Route path=":accountId/users" element={<AccountUsers />} />
      <Route path=":accountId/settings" element={<AccountSettings />} />
      <Route
        path=":accountId/branches/:branchId/users"
        element={<ManagerUsers />}
      />
      <Route
        path=":accountId/branches/:branchId/groups"
        element={<ManagerGroups />}
      />
      <Route
        path=":accountId/branches/:branchId/inventory"
        element={<ManagerInventory />}
      />
      <Route
        path=":accountId/branches/:branchId/inbound_inventories/:inboundInventoryId"
        element={<ManagerInboundInventory />}
      />
      <Route
        path=":accountId/branches/:branchId/inbound_inventories"
        element={<ManagerInboundInventories />}
      />
      <Route
        path=":accountId/branches/:branchId/plan/:productId?"
        element={<Plan />}
      />
      <Route
        path=":accountId/branches/:branchId/locations"
        element={<Locations />}
      />
      <Route
        path=":accountId/branches/:branchId/locations-add"
        element={<LocationForm />}
      />
      <Route
        path=":accountId/branches/:branchId/locations/:locationId"
        element={<LocationInventory />}
      />
      <Route
        path=":accountId/branches/:branchId/locations/:locationId/edit"
        element={<LocationForm />}
      />
      <Route
        path=":accountId/branches/:branchId/cycle_counts"
        element={<CycleCounts />}
      />
      <Route
        path=":accountId/branches/:branchId/cycle_counts/:cycleCountId"
        element={<CycleCountEdit />}
      />
      <Route
        path=":accountId/branches/:branchId/orders/:orderId"
        element={<ManagerOrder />}
      />
      <Route
        path=":accountId/branches/:branchId/orders"
        element={<ManagerOrders />}
      />
      <Route
        path=":accountId/branches/:branchId/requests"
        element={<BranchRequestsRedirect />}
      />
      <Route
        path=":accountId/branches/:branchId"
        element={<BranchRootRedirect />}
      />
    </Route>
    {user.role === 'internal' && (
      <>
        <Route path="/orders" element={<Orders />} />
        <Route path="/orders/:id" element={<Order />} />
        {/*<Route path="/invoices" element={<Invoices />} />*/}
      </>
    )}
    <Route path="/plan-exports" element={<PlanExportsEmbed />} />
    <Route path="/users" element={<InternalUsers />} />
    <Route path="/messages" element={<InternalMessages />} />
    <Route path="/product-attributes" element={<Species />} />
    <Route path="/docs" element={<Docs />} />
    <Route path="/design-catalog" element={<DesignCatalog />} />
    <Route path="/" element={<Navigate to="/clients" replace />} />
  </Routes>
)

const App: React.FunctionComponent = () => {
  return (
    <Provider store={store}>
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <Meta>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="*"
              element={
                <Auth>
                  {(user) => (
                    <div className="app-shell">
                      <Header />
                      <div className="app-shell_body">
                        {user.role === 'manager' ? (
                          <ManagerRoutes user={user} />
                        ) : (
                          <InternalRoutes user={user} />
                        )}
                      </div>
                      <WebChat />
                    </div>
                  )}
                </Auth>
              }
            />
          </Routes>
        </Meta>
      </BrowserRouter>
    </Provider>
  )
}

export default App
// tslint:enable: max-line-length
